import {Box, Flex, Text, VStack} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {SyntheseEGalim} from 'api-types/ffconnect';
import {toDecimalFr} from 'ff-common';
import {EgalimIcon} from '@/components/svg/EgalimIcon';

/**
 * Bloc synthèse du dashboard ÉGalim.
 * Affiche les montants (total ÉGalim, dont BIO, total d'achat)
 * et trois barres de progression : Bio (vert), ÉGalim (bleu), référence totale (gris).
 */
export const EgalimSynthese: React.FC<{synthese: SyntheseEGalim}> = ({
  synthese,
}) => {
  const {t} = useTranslation();

  // Extraction des données de la synthèse avec des valeurs par défaut pour éviter les undefined
  const montantTotal =
    synthese.montant_total_net_facture ?? synthese.montant_total ?? 0;
  const montantBio = synthese.montant_bio ?? 0;
  const montantEgalim = synthese.montant_egalim ?? 0;
  const pourcentageBio = synthese.pourcentage_bio ?? 0;
  const pourcentageEgalim = synthese.pourcentage_egalim ?? 0;

  return (
    <VStack
      align='stretch'
      spacing={4}
      mt={4}
    >
      {/* Ligne montants */}
      <Flex
        align='baseline'
        gap={{base: 4, md: 8}}
        wrap='wrap'
      >
        {/* Total Montant */}
        <Box>
          <Text
            fontSize='sm'
            color='gray.dark'
          >
            {t('egalim.montant_achat_total')}
          </Text>
          <Text
            fontSize='2xl'
            fontWeight='bold'
            color='gray.dark'
          >
            {t('egalim.montant_euro', {
              montant: toDecimalFr(montantTotal, 0, 0),
            })}
          </Text>
        </Box>

        {/* Total ÉGalim */}
        <Box>
          <Text
            fontSize='sm'
            color='blue.main'
          >
            {t('egalim.dont_egalim')}
          </Text>
          <Text
            fontSize='2xl'
            fontWeight='bold'
            color='blue.main'
          >
            {t('egalim.montant_euro', {
              montant: toDecimalFr(montantEgalim, 0, 0),
            })}
          </Text>
        </Box>

        {/* dont BIO */}
        <Box>
          <Flex
            align='center'
            gap={1}
          >
            <Text
              fontSize='sm'
              color='green.egalim'
            >
              {t('egalim.dont_bio')}
            </Text>
            <EgalimIcon
              color='green.egalim'
              width='14px'
              height='14px'
            />
          </Flex>
          <Text
            fontSize='2xl'
            fontWeight='bold'
            color='green.egalim'
          >
            {t('egalim.montant_euro', {montant: toDecimalFr(montantBio, 0, 0)})}
          </Text>
        </Box>
      </Flex>

      {/* Barres de progression */}
      <VStack
        align='stretch'
        spacing={2}
        mt={2}
      >
        {/* Barre Bio */}
        <Flex
          align='center'
          gap={2}
        >
          <Box flex='1'>
            <Box
              h='15px'
              w={`${Math.min(pourcentageBio, 100)}%`}
              bg='green.egalim'
              borderRadius='3px'
              minW='4px'
            />
          </Box>
          <Text
            fontSize='sm'
            color='green.egalim'
            whiteSpace='nowrap'
            minW='120px'
            textAlign='right'
          >
            {t('egalim.pourcentage_bio', {
              pourcentage: toDecimalFr(pourcentageBio, 0, 0),
            })}
          </Text>
        </Flex>

        {/* Barre Egalim */}
        <Flex
          align='center'
          gap={2}
        >
          <Box flex='1'>
            <Box
              h='15px'
              w={`${Math.min(pourcentageEgalim, 100)}%`}
              bg='blue.main'
              borderRadius='3px'
              minW='4px'
            />
          </Box>
          <Text
            fontSize='sm'
            color='blue.main'
            whiteSpace='nowrap'
            minW='120px'
            textAlign='right'
          >
            {t('egalim.pourcentage_egalim', {
              pourcentage: toDecimalFr(pourcentageEgalim, 0, 0),
            })}
          </Text>
        </Flex>
      </VStack>
    </VStack>
  );
};
